/**
 * A business's offers: the service fulfilment configs it publishes under the platform services,
 * mirroring VetGeo's booking-configs and config-assignments services. An offer is found by the
 * homepage's deals map only when it is Active, linked to a resource with a duration, and its
 * organisation has a location (OlaCore's Read_ServiceFulfilmentConfigs_Platform joins all three).
 */
import { API_BASE_URL } from './apiService';

/** Core enums start at 1 = Unknown, so 0 or missing is never a valid value. */
export const ACTIVATION_STATE = { UNKNOWN: 1, ACTIVE: 2, INACTIVE: 3 };
const PREPAYMENT_NOT_REQUIRED = 3;
const CONFIG_TYPE_BASIC = 2;
const QUEUE_NOT_ACTIVE = 2;
const RESOURCE_PERFORMS_SERVICE = 2;
const RESOURCE_REQUIRED = 3;

/** Core's ENUM_Cmd_Status.ok: the API answers business errors with HTTP 200 and another status. */
const STATUS_OK = 1;

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

export class ApiError extends Error {}

const post = async (endpoint, payload, accessToken) => {
  const headers = { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const response = await fetch(`${API_BASE_URL}/api/${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload ?? {}),
  });
  if (!response.ok) throw new ApiError(`${endpoint} failed: ${response.status}`);
  const body = await response.json();
  if (body && body.func_status !== undefined && body.func_status !== STATUS_OK) {
    throw new ApiError(`${endpoint} was refused: ${body.func_msg || body.func_status}`);
  }
  return body;
};

const newId = (body) => Number(body?.newRecordID ?? body?.NewRecordID ?? 0);

/** The platform services offers are published under (the 38 seeded categories' services). */
export const readPlatformServices = async (accessToken) => {
  const body = await post('Read_All_Supported_Platform_Services', {}, accessToken);
  return (body.platformServiceList || []).map((s) => ({ serviceId: Number(s.serviceId), name: String(s.name || '') }));
};

const toOffer = (c) => ({
  serviceFulfilmentConfigId: Number(c.serviceFulfilmentConfigId ?? 0),
  orgId: Number(c.orgId ?? 0),
  serviceId: Number(c.serviceId ?? 0),
  name: String(c.name ?? ''),
  isActive: Number(c.isActive ?? ACTIVATION_STATE.UNKNOWN),
  monetaryAmount: Number(c.monetaryAmount ?? 0),
});

export const readOrgOffers = async (orgId, accessToken) => {
  const body = await post('Read_All_ServiceFulfilmentConfigs_By_Org_ID', { orgId: Number(orgId) }, accessToken);
  return (body.serviceFulfilmentConfigCompleteList || []).map(toOffer);
};

/** The config payload exactly as VetGeo sends it; isActive decides whether it is published. */
const offerPayload = ({ orgId, serviceId, name, price, isActive }) => ({
  orgId: Number(orgId),
  contactId: 0,
  serviceId: Number(serviceId),
  name,
  isActive,
  numberOfRequiredResources: 0,
  prePaymentRequired: PREPAYMENT_NOT_REQUIRED,
  monetaryAmount: Number(price) || 0,
  maxOrdersPerDay: 0,
  maxDailyLimit: 0,
  advancedOrBasicServiceFulfilmentConfig: CONFIG_TYPE_BASIC,
  isQueueActive: QUEUE_NOT_ACTIVE,
});

/** A new offer starts unpublished; publishOffer makes it findable. Returns its id. */
export const createOffer = async (offer, accessToken) =>
  newId(await post('Create_ServiceFulfilmentConfig', offerPayload({ ...offer, isActive: ACTIVATION_STATE.INACTIVE }), accessToken));

export const updateOffer = async (offer, accessToken) =>
  post('Update_Org_ServiceFulfilmentConfig', {
    ...offerPayload(offer),
    serviceFulfilmentConfigId: Number(offer.serviceFulfilmentConfigId),
  }, accessToken);

export const readOrgResources = async (orgId, accessToken) => {
  const body = await post('Read_All_Organisation_Resources', { orgId: Number(orgId) }, accessToken);
  return (body.resourceList || []).map((r) => ({ resourceId: Number(r.resourceId), resourceName: String(r.resourceName || '') }));
};

/** OlaCore enums and limits a resource is validated against (CoreValidation, IResourceOptions). */
const BOOKING_NO_OVERLAPPING = 2;
const TIME_ALLOCATION_FREE = 4;
const MEASUREMENT_CM = 3;
const MAX_APPOINTMENT_OR_EXCEPTION_MS = 14 * MS_PER_DAY; // GeneralConfig.MAX_AP_EX_TSO_DURATION
const MAX_FUTURE_DAYS = 365; // GeneralConfig.MAX_GENERATE_FUTURE_IN_DAYS
const MAX_DAILY_USER_SLOTS = 4 * 24; // validated 0-96

/**
 * The resource offers are performed by: one per business, at its location, created on first save.
 * The values are the ones OlaCore gives the resource it creates for every new user (SC_SYS_System
 * CreateNewUser), so they pass its validation: free allocation (no fixed slots, so slotDuration 0),
 * no overlapping bookings, the two-week maximum durations, a year ahead. The time zone must be empty
 * on create; OlaCore works it out from the location. A resource with nothing booked is free all the
 * time; opening hours would be blocked periods later.
 */
export const createDefaultResource = async (org, accessToken) =>
  newId(await post('Create_Resource', {
    orgId: Number(org.orgId),
    userId: 0,
    resourceName: org.name,
    latitude: Number(org.latitude),
    longitude: Number(org.longitude),
    timeZoneIANA: '',
    allowsOverlaps: BOOKING_NO_OVERLAPPING,
    timeAllocationType: TIME_ALLOCATION_FREE,
    slotDuration: 0,
    maxAppointmentDuration: MAX_APPOINTMENT_OR_EXCEPTION_MS,
    maxExceptionDuration: MAX_APPOINTMENT_OR_EXCEPTION_MS,
    maxDailyUserSlots: MAX_DAILY_USER_SLOTS,
    maxAppointmentFutureTimeInMs: MAX_FUTURE_DAYS * MS_PER_DAY,
    widthValue: 0,
    widthUnitType: MEASUREMENT_CM,
    heightValue: 0,
    heightUnitType: MEASUREMENT_CM,
    depthValue: 0,
    depthUnitType: MEASUREMENT_CM,
  }, accessToken));

export const readOfferLinks = async (orgId, serviceFulfilmentConfigId, accessToken) => {
  const body = await post('Read_All_ServiceFulfilmentConfigResourceMaps_By_ServiceFulfilmentConfig_ID',
    { orgId: Number(orgId), serviceFulfilmentConfigId: Number(serviceFulfilmentConfigId) }, accessToken);
  return (body.serviceFulfilmentConfigResourceMapList || []).map((m) => ({
    serviceFulfilmentConfigResourceMapId: Number(m.serviceFulfilmentConfigResourceMapId ?? 0),
    resourceId: Number(m.resourceId ?? 0),
    durationMinutes: Math.round(Number(m.durationMilliseconds ?? 0) / MS_PER_MINUTE),
    price: Number(m.monetaryAmount ?? 0),
  }));
};

const linkPayload = ({ orgId, serviceId, serviceFulfilmentConfigId, resourceId, durationMinutes, price, monetaryCurrency }) => ({
  orgId: Number(orgId),
  serviceId: Number(serviceId),
  serviceFulfilmentConfigId: Number(serviceFulfilmentConfigId),
  resourceId: Number(resourceId),
  durationMilliseconds: Math.round(Number(durationMinutes)) * MS_PER_MINUTE,
  monetaryAmount: Number(price) || 0,
  // The price's currency is the business's own; OlaCore refuses a price without one (IMoneyValue).
  monetaryCurrency: Number(monetaryCurrency),
  serviceFulfilmentResourceConfigRelationship: RESOURCE_PERFORMS_SERVICE,
  serviceFulfilmentResourceConfigRequiredOptional: RESOURCE_REQUIRED,
});

export const hasLocation = (org) => Number(org?.latitude) !== 0 || Number(org?.longitude) !== 0;

/** The organisation's resource: the first one it has, or one created at its location. */
const ensureResource = async (org, accessToken) => {
  const resources = await readOrgResources(org.orgId, accessToken);
  const resourceId = resources.length > 0 ? resources[0].resourceId : await createDefaultResource(org, accessToken);
  if (!resourceId) throw new ApiError('Your business could not be set up for bookings. Please try again.');
  return resourceId;
};

/** Link the offer to the resource with its length and price, or update the link it already has. */
const ensureLink = async (org, offer, { durationMinutes, price }, accessToken) => {
  const resourceId = await ensureResource(org, accessToken);
  const link = { orgId: org.orgId, serviceId: offer.serviceId, serviceFulfilmentConfigId: offer.serviceFulfilmentConfigId, resourceId, durationMinutes, price, monetaryCurrency: org.monetaryCurrency };
  const existing = (await readOfferLinks(org.orgId, offer.serviceFulfilmentConfigId, accessToken)).find((l) => l.resourceId === resourceId);
  if (existing) {
    await post('Update_ServiceFulfilmentConfigResourceMap', {
      ...linkPayload(link),
      serviceFulfilmentConfigResourceMapId: existing.serviceFulfilmentConfigResourceMapId,
    }, accessToken);
  } else {
    await post('Create_ServiceFulfilmentConfigResourceMap', linkPayload(link), accessToken);
  }
};

/**
 * Save an offer: the offer itself (new ones start unpublished, edits keep their state) and its link to
 * the business's resource, which is where its length and price live. Returns the offer's id.
 */
export const saveOffer = async (org, offer, accessToken) => {
  if (!hasLocation(org)) throw new ApiError('Add your business location first: customers find offers by where you are.');
  if (!(Number(offer.durationMinutes) > 0)) throw new ApiError('Give the offer a length in minutes: a booking with no duration ends when it starts.');
  if (Number(offer.durationMinutes) * MS_PER_MINUTE > MAX_APPOINTMENT_OR_EXCEPTION_MS) throw new ApiError('An offer can last at most two weeks.');

  let serviceFulfilmentConfigId = Number(offer.serviceFulfilmentConfigId) || 0;
  if (serviceFulfilmentConfigId) {
    await updateOffer({ ...offer, orgId: org.orgId }, accessToken);
  } else {
    serviceFulfilmentConfigId = await createOffer({ ...offer, orgId: org.orgId }, accessToken);
    if (!serviceFulfilmentConfigId) throw new ApiError('The offer could not be created. Please try again.');
  }
  await ensureLink(org, { ...offer, serviceFulfilmentConfigId }, offer, accessToken);
  return serviceFulfilmentConfigId;
};

/**
 * Publish or unpublish: the deals map shows Active offers that are linked to a resource. Publishing an
 * offer with no link would make it Active but still invisible, so that is refused with a reason.
 */
export const setPublished = async (org, offer, published, accessToken) => {
  if (published) {
    if (!hasLocation(org)) throw new ApiError('Add your business location before publishing: offers are found by where you are.');
    const links = await readOfferLinks(org.orgId, offer.serviceFulfilmentConfigId, accessToken);
    if (!links.some((l) => l.durationMinutes > 0)) throw new ApiError('Edit the offer and save its length first, then publish it.');
  }
  await updateOffer({
    ...offer,
    orgId: org.orgId,
    price: offer.monetaryAmount,
    isActive: published ? ACTIVATION_STATE.ACTIVE : ACTIVATION_STATE.INACTIVE,
  }, accessToken);
};

/** Radius a visit counts within when the rule does not set one (the API's default). */
export const DEFAULT_VISIT_RADIUS_METERS = 500;

/**
 * The offer's visit rule: customers unlock the offer after visiting the business often enough.
 * Null when the offer has none, which means it is available to everyone nearby.
 */
export const readVisitRule = async (orgId, serviceFulfilmentConfigId, accessToken) => {
  const body = await post('Read_Offer_Qualification_Rule',
    { orgId: Number(orgId), serviceFulfilmentConfigId: Number(serviceFulfilmentConfigId) }, accessToken);
  const rule = body.rule;
  if (!rule) return null;
  return {
    minVisits: Number(rule.minVisits),
    windowDays: Number(rule.windowDays),
    maxDistanceMeters: rule.maxDistanceMeters == null ? null : Number(rule.maxDistanceMeters),
    distinctDaysOnly: Boolean(rule.distinctDaysOnly),
  };
};

/**
 * Save the offer's visit rule, checked against the limits the API enforces (Save_Offer_Qualification_Rule):
 * 1-1000 visits within 1-365 days, and a radius of 1-99999 m or none (the 500 m default).
 */
export const saveVisitRule = async (orgId, serviceFulfilmentConfigId, rule, accessToken) => {
  const minVisits = Number(rule.minVisits);
  const windowDays = Number(rule.windowDays);
  const radius = rule.maxDistanceMeters === '' || rule.maxDistanceMeters == null ? null : Number(rule.maxDistanceMeters);
  if (!Number.isInteger(minVisits) || minVisits < 1 || minVisits > 1000) throw new ApiError('Visits needed must be a whole number from 1 to 1000.');
  if (!Number.isInteger(windowDays) || windowDays < 1 || windowDays > 365) throw new ApiError('The number of days must be a whole number from 1 to 365.');
  if (radius !== null && (!Number.isInteger(radius) || radius < 1 || radius > 99999)) throw new ApiError('The visit radius must be a whole number of metres from 1 to 99999.');
  return post('Save_Offer_Qualification_Rule', {
    orgId: Number(orgId),
    serviceFulfilmentConfigId: Number(serviceFulfilmentConfigId),
    minVisits,
    windowDays,
    maxDistanceMeters: radius,
    distinctDaysOnly: Boolean(rule.distinctDaysOnly),
  }, accessToken);
};
